const PAGE_WINDOW = 9
const WINDOW_HALF = 4

/**
 * Offset pagination window generator (nine page buttons by default).
 *
 * Rules:
 * - If `totalPages <= 9`, always show every page with no compression.
 * - If the API already gives a known `totalPages`, use that total to build a centered/sliding window.
 * - `hasDeepOffset` / `lookAheadPages` are only used to estimate a temporary reachable total when
 *   the backend has not yet provided the full page count.
 *
 * @param {number} totalPages
 * @param {number} currentPage 1-based
 * @param {boolean} hasNextPage
 * @param {boolean} hasDeepOffset
 * @param {number} [lookAheadPages=8] used with deep offset (8 or 4 from API)
 * @returns {number[]}
 */
export function getOffsetPageNumbers(
  totalPages,
  currentPage,
  hasNextPage,
  hasDeepOffset,
  lookAheadPages = 8,
) {
  const total = Math.max(0, Number(totalPages) || 0)
  const currentRaw = Math.max(1, Number(currentPage) || 1)
  const hasNext = Boolean(hasNextPage)
  const hasDeep = Boolean(hasDeepOffset)
  const lookAhead = hasDeep ? Math.max(0, Number(lookAheadPages) || 0) : 0

  const minReachableTotal = hasNext
    ? currentRaw + (hasDeep ? lookAhead : 1)
    : currentRaw

  const effectiveTotal = hasNext
    ? Math.max(total, minReachableTotal)
    : Math.max(total, currentRaw)

  if (effectiveTotal <= 0) return []
  if (effectiveTotal <= PAGE_WINDOW) {
    return Array.from({ length: effectiveTotal }, (_, i) => i + 1)
  }

  const current = Math.min(currentRaw, effectiveTotal)
  let start = Math.max(1, current - WINDOW_HALF)
  const maxStart = Math.max(1, effectiveTotal - PAGE_WINDOW + 1)
  start = Math.min(start, maxStart)
  const end = Math.min(effectiveTotal, start + PAGE_WINDOW - 1)

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}
